import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowUpDown,
	Calendar,
	Clock,
	Eye,
	FileText,
	Loader2,
	Pencil,
} from "lucide-react";
import { useMemo, useState } from "react";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { useAuth } from "@/lib/auth";
import { useTRPC } from "@/integrations/trpc/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/vendor/$buyerSlug/bids/")({
	component: VendorBidsPage,
});

function getBidStatusColor(status: string) {
	switch (status) {
		case "draft":
			return "bg-yellow-100 text-yellow-700";
		case "submitted":
			return "bg-blue-100 text-blue-700";
		case "withdrawn":
			return "bg-gray-100 text-gray-700";
		case "amended":
			return "bg-purple-100 text-purple-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
}

function formatDate(dateStr: string | null): string {
	if (!dateStr) return "-";
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function getDaysUntilDeadline(dueDate: string | null): number | null {
	if (!dueDate) return null;
	const due = new Date(dueDate);
	const now = new Date();
	const diffTime = due.getTime() - now.getTime();
	return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function VendorBidsPage() {
	const buyer = useBuyerContext();
	const { buyerSlug } = Route.useParams();
	const { user } = useAuth();
	const vendorEmail = user?.email ?? "";
	const trpc = useTRPC();

	const [sortField, setSortField] = useState<"date" | "status">("date");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

	// Fetch vendor's bids
	const { data: bidsData, isLoading: bidsLoading } = useQuery(
		trpc.vendorPortal.bids.list.queryOptions({
			vendorEmail,
		}),
	);

	// Fetch public solicitations for this buyer to get titles/details
	const { data: solicitationsData, isLoading: solicitationsLoading } = useQuery(
		trpc.public.solicitations.list.queryOptions({
			buyerOrgId: buyer.id,
			limit: 200,
			offset: 0,
		}),
	);

	// Create a map of solicitation ID to solicitation data
	const solicitationsMap = useMemo(() => {
		const map = new Map<string, {
			title: string;
			due_date: string | null;
			status: string;
			type: string | null;
		}>();
		for (const sol of solicitationsData ?? []) {
			map.set(sol.id, {
				title: sol.title ?? "Untitled Solicitation",
				due_date: sol.due_date,
				status: sol.status,
				type: sol.type,
			});
		}
		return map;
	}, [solicitationsData]);

	// Combine bids with solicitation data and filter to only this buyer's solicitations
	const vendorBids = useMemo(() => {
		if (!bidsData) return [];

		return bidsData
			.filter((bid) => solicitationsMap.has(bid.solicitation_id))
			.map((bid) => {
				const solicitation = solicitationsMap.get(bid.solicitation_id);
				return {
					...bid,
					solicitationTitle: solicitation?.title ?? "Unknown Solicitation",
					solicitationDueDate: solicitation?.due_date ?? null,
					solicitationStatus: solicitation?.status ?? null,
					solicitationType: solicitation?.type ?? null,
					isDraft: bid.status === "draft",
				};
			});
	}, [bidsData, solicitationsMap]);

	// Sort bids
	const sortedBids = useMemo(() => {
		return [...vendorBids].sort((a, b) => {
			let comparison = 0;
			switch (sortField) {
				case "date": {
					const dateA = new Date(a.submitted_at || a.updated_at || a.created_at);
					const dateB = new Date(b.submitted_at || b.updated_at || b.created_at);
					comparison = dateA.getTime() - dateB.getTime();
					break;
				}
				case "status":
					comparison = a.status.localeCompare(b.status);
					break;
			}
			return sortDirection === "desc" ? -comparison : comparison;
		});
	}, [vendorBids, sortField, sortDirection]);

	const stats = useMemo(() => ({
		total: vendorBids.length,
		drafts: vendorBids.filter((b) => b.status === "draft").length,
		submitted: vendorBids.filter((b) => b.status === "submitted").length,
		withdrawn: vendorBids.filter((b) => b.status === "withdrawn").length,
	}), [vendorBids]);

	const isLoading = bidsLoading || solicitationsLoading;

	if (isLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div>
					<h1 className="text-3xl font-bold tracking-tight">My Bids</h1>
					<p className="text-muted-foreground">
						Manage your bid submissions and drafts for {buyer.name}
					</p>
				</div>

				{/* Stats */}
				<div className="grid gap-4 md:grid-cols-4">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">Total Bids</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.total}</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Clock className="h-4 w-4 text-yellow-500" />
								<span className="text-sm text-muted-foreground">Drafts</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.drafts}</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-blue-500" />
								<span className="text-sm text-muted-foreground">Submitted</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.submitted}</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-gray-500" />
								<span className="text-sm text-muted-foreground">Withdrawn</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.withdrawn}</div>
						</CardContent>
					</Card>
				</div>

				{/* Bids Table */}
				<Card>
					<CardHeader>
						<CardTitle>All Bids</CardTitle>
						<CardDescription>
							{sortedBids.length} bid
							{sortedBids.length !== 1 ? "s" : ""} found
						</CardDescription>
					</CardHeader>
					<CardContent>
						{sortedBids.length === 0 ? (
							<div className="text-center py-12 text-muted-foreground">
								<FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
								<p>No bids found</p>
								<p className="text-sm mt-1">
									Browse <Link to="/vendor/$buyerSlug/solicitations" params={{ buyerSlug }} className="text-primary hover:underline">available solicitations</Link> to start bidding
								</p>
							</div>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Solicitation</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Deadline</TableHead>
										<TableHead
											className="cursor-pointer hover:text-primary"
											onClick={() => {
												if (sortField === "date") {
													setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
												} else {
													setSortField("date");
													setSortDirection("desc");
												}
											}}
										>
											<div className="flex items-center gap-1">
												Last Updated
												<ArrowUpDown className="h-3 w-3" />
											</div>
										</TableHead>
										<TableHead className="text-right">Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{sortedBids.map((bid) => {
										const daysLeft = getDaysUntilDeadline(bid.solicitationDueDate);
										const isUrgent = daysLeft !== null && daysLeft > 0 && daysLeft <= 7 && bid.isDraft;

										return (
											<TableRow key={bid.id}>
												<TableCell>
													<div className="font-medium">
														{bid.solicitationTitle}
													</div>
													{bid.amendment_number > 0 && (
														<span className="text-xs text-muted-foreground">
															Amendment #{bid.amendment_number}
														</span>
													)}
												</TableCell>
												<TableCell>
													<Badge className={getBidStatusColor(bid.status)}>
														{bid.status}
													</Badge>
												</TableCell>
												<TableCell>
													<div className="flex items-center gap-2">
														<Calendar className="h-3 w-3 text-muted-foreground" />
														<span>{formatDate(bid.solicitationDueDate)}</span>
														{isUrgent && (
															<Badge variant="destructive" className="text-xs">
																{daysLeft}d left
															</Badge>
														)}
													</div>
												</TableCell>
												<TableCell>
													<span className="text-sm text-muted-foreground">
														{formatDate(bid.submitted_at || bid.updated_at)}
													</span>
												</TableCell>
												<TableCell className="text-right">
													{bid.isDraft ? (
														<Button size="sm" asChild>
															<Link
																to="/vendor/$buyerSlug/bids/$bidId/edit"
																params={{ buyerSlug, bidId: bid.id }}
															>
																<Pencil className="mr-1 h-3 w-3" />
																Continue
															</Link>
														</Button>
													) : (
														<Button variant="outline" size="sm" asChild>
															<Link
																to="/vendor/$buyerSlug/bids/$bidId"
																params={{ buyerSlug, bidId: bid.id }}
															>
																<Eye className="mr-1 h-3 w-3" />
																View
															</Link>
														</Button>
													)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						)}
					</CardContent>
				</Card>
			</div>
		</VendorLayout>
	);
}
